import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from "react-router-dom";

import { useTranslation } from "core/hooks/useTranslation";
import usePermissions from 'core/hooks/usePermission';
import useRole from 'core/hooks/useRole';

import SubTabTableDashboardLayout from "components/layouts/tab/subtab/table";
import Table from 'components/common/table';
import AddMobileUrl from "./mobileUrl-add.component";

import { tableColumnsCreator } from 'utils/tableColumnsCreator';

import { PERMISSION_RESOURCE, PERMISSION_ACTION } from 'core/constants/permission';
import { getTableColumns } from './columns';

import useMobileUrls from './useMobileUrls';

import campaignService from 'core/services/apiServices/campaignService';
import autosuggestionService from 'core/services/apiServices/autosuggestionService';
import { useGlobalSelector, userInfoSelector } from "core/stores/globalStore";
import { USER_ROLE } from "core/constants/auth";

const ROW_UNIQUE_KEY_PROP = "id";

const CampaignMobileUrls = () => {
    const { t } = useTranslation();

    const permissionUtils = usePermissions();

    const searchParams = useParams();
    const { search } = useLocation();  

    const [ isSaving , setIsSaving ] = useState(false)

    const [addPopupData, setAddPopupData] = useState({
        isVisible: false
    });

    const campaignId = searchParams.id;
    const brandId = (new URLSearchParams(search)).get("brandId");

    const [ mobileUrls, setMobileUrls ] = useState([]);

    const userInfo = useGlobalSelector(userInfoSelector);
    const { hasRole } = useRole();

    const isAffiliateManager = hasRole(USER_ROLE.AFFILIATE_MANAGER);
    const hasCampaignManagementAccess = userInfo.campaignManagement;

    const hasModifyPermission = isAffiliateManager ? hasCampaignManagementAccess : permissionUtils.has( PERMISSION_RESOURCE.MARKETING_TOOLS_CAMPAIGN, PERMISSION_ACTION.MODIFY);

    const {
        loading,
        urls,
        addMobileUrl,
        removeMobileUrl
    } = useMobileUrls(campaignId)

    const onMobileUrlSave = useCallback(urls => {
        addMobileUrl(urls)
    }, [addMobileUrl])

    const handleMobileUrlDelete = url => {
        setIsSaving(true)
        campaignService.deleteMobileUrl({
            campaignId,
            mobileUrlId: url.id
        })
            .then(() => removeMobileUrl( url.id ))
            .finally(() => setIsSaving(false))
    }

    const showAddPopup = () => {
        setAddPopupData({
            isVisible: true
        })
    }

    const hideAddPopup = useCallback(() => {
        setAddPopupData({
            isVisible: false
        })
    }, [setAddPopupData])


    const { mainTableColumns } = useMemo(() => {
        return tableColumnsCreator({
            mainColumns: getTableColumns,
            additionalProps: {
                mobileUrls
            },
            t
        });
    }, [t, mobileUrls])

    const availableMobileUrls = useMemo(() => {
        return mobileUrls.filter(url => urls.every( l => l.id !== url.id))
    }, [urls, mobileUrls])

    const tableRowActions = [];

    if (hasModifyPermission) {
        tableRowActions.push({
            title: t('backoffice.common.delete'),
            confirmationMessage: t("backoffice.campaigns.doYouWanttoDeleteMobileUrl"),
            confirmationTitle: t("backoffice.campaigns.deleteMobileUrl"),
            icon: "trash",
            showConfirmation: true,
            onClick: handleMobileUrlDelete
        })
    }

    const headerPartsData = {
        button: {
            icon: "plus",
            type: "primary",
            onClick: () => showAddPopup(null),
            text: t("backoffice.campaigns.addUrl"),
            enabled: hasModifyPermission,
            disabled: availableMobileUrls.length === 0
        }
    }

    useEffect(() => {
        autosuggestionService.getBrandMobileUrls({ brandId })
            .then(data => {
                setMobileUrls(data)
            })
    }, [])

    

    return (
        <SubTabTableDashboardLayout
            header={headerPartsData}
        >
            <Table
                loading={loading || isSaving}
                columns={mainTableColumns}
                data={urls}
                uniqueKey={ROW_UNIQUE_KEY_PROP}
                disableFullView={true}
                actions={tableRowActions}
                noPagination={true}
            />

            <AddMobileUrl
                isVisible={addPopupData.isVisible}
                mobileUrls={availableMobileUrls}
                onSave={onMobileUrlSave}
                onClose={hideAddPopup}
            />
        </SubTabTableDashboardLayout>
    );
}

export default CampaignMobileUrls;